import {
  countMetricOptions,
  Goal,
  ID,
  ImpactLevel,
  initialDateFormat,
  LifeArea,
  Timestamp,
} from '@griply/types';
import {
  getCompletionPercentage,
  getGoalCompletion,
  getLinkedItemColor,
  getStartTillEndLabel,
  getSubGoals,
  getWeekStartsOn,
} from '@griply/utils';
import { differenceInDays, isBefore, isSameDay } from 'date-fns';
import { t } from 'i18next';
import React, { useMemo, useRef } from 'react';
import {
  Check,
  CheckCircle2,
  ChevronLeft,
  FlagFilled,
  IconFormIcons,
  Lock,
  Target,
  ThunderLightningFilled,
} from 'shared/assets/icons';
import { GoalContextMenu } from 'shared/components/connected/goal-context-menu';
import { CircleProgress } from 'shared/components/ui/circle-progress';
import {
  DatepickerWithOptions,
  IconOptions,
} from 'shared/components/ui/datepicker-with-options';
import { Icon } from 'shared/components/ui/icon';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { ImpactPickerMenu } from 'shared/components/ui/impact-picker-menu';
import { TimeLeft } from 'shared/components/ui/time-left';
import { Timeframe } from 'shared/components/ui/timeframe';
import { useDragPreview } from 'shared/components/ui/virtualized-tree-list';
import { useOpenGoalMetricsForm } from 'shared/contexts/goal-metrics-form';
import { useToday } from 'shared/contexts/today';
import { useUpdateGoalMutation } from 'shared/hooks/queries/use-update-goal-mutation';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { useUser } from 'shared/hooks/use-user';
import { ContextMenuOptions } from 'shared/views/goal-filter-list/types';
import { useTheme } from 'styled-components';

import { CtaContainer } from './cta-container';
import * as Styled from './goal-list-card.style';

export type GoalListCardProps = {
  goal: Goal;
  onClick?: (id: ID) => void;
  onUpdateProgress?: (id: ID) => void;

  lifeAreas: LifeArea[];
  goals: Goal[];

  hideLifeArea?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: (e: React.MouseEvent) => void;
  hasChildren?: boolean;
  isSelected?: boolean;
  isDropTarget?: boolean;
  preventInteraction?: boolean;
  hideContextMenu?: boolean;
  excludedContextMenuOptions?: ContextMenuOptions[];
};

export const GoalListCard: React.FC<GoalListCardProps> = ({
  goal,
  onClick,
  onUpdateProgress,
  goals,
  lifeAreas,
  hideLifeArea,
  isExpanded,
  onToggleExpand,
  hasChildren,
  isSelected,
  isDropTarget,
  preventInteraction,
  hideContextMenu,
  excludedContextMenuOptions,
}) => {
  const theme = useTheme();
  const today = useToday();
  const user = useUser();
  const isDragPreview = useDragPreview();
  const { mutate: updateGoal } = useUpdateGoalMutation();
  const startDateRef = useRef<HTMLSpanElement>(null);
  const deadlineRef = useRef<HTMLSpanElement>(null);
  const impactRef = useRef<HTMLSpanElement>(null);
  const {
    menuOpen: startDateMenuOpen,
    openMenu: openStartDateMenu,
    closeMenu: closeStartDateMenu,
  } = useOpenMenu();
  const {
    menuOpen: deadlineMenuOpen,
    openMenu: openDeadlineMenu,
    closeMenu: closeDeadlineMenu,
  } = useOpenMenu();
  const {
    menuOpen: impactMenuOpen,
    openMenu: openImpactMenu,
    closeMenu: closeImpactMenu,
  } = useOpenMenu();

  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState({
    disabled: !!goal.frozenAt || hideContextMenu,
  });

  const showSelection = isSelected || !!menuPosition;

  const _onUpdateProgress = (e: React.MouseEvent) => {
    e.preventDefault();
    onUpdateProgress?.(goal.id);
    closeMenu();
  };

  const color = useMemo(
    () => getLinkedItemColor(goal.id, { goals, lifeAreas }),
    [goal.id, goals, lifeAreas],
  );

  const { start, end, current } = useMemo(
    () =>
      getGoalCompletion(goal, {
        goals,
      }),
    [goal, goals],
  );

  const completionPercentage = useMemo(
    () => getCompletionPercentage({ start, end, current }),
    [current, end, start],
  );

  const uncompletedTasks = useMemo(() => {
    const subGoals = getSubGoals(goal.id, { goals, recursive: true });
    const { total, completed } = subGoals.reduce(
      (acc, subGoal) =>
        subGoal.archivedAt
          ? acc
          : {
              total: acc.total + (subGoal.taskCount ?? 0),
              completed: acc.completed + (subGoal.completedTaskCount ?? 0),
            },
      { total: goal.taskCount ?? 0, completed: goal.completedTaskCount ?? 0 },
    );

    return Math.max(0, total - completed);
  }, [goal.completedTaskCount, goal.id, goal.taskCount, goals]);

  const lifeArea = useMemo(
    () => lifeAreas.find(({ id }) => id === goal.lifeAreaId),
    [goal.lifeAreaId, lifeAreas],
  );
  const started =
    !goal.startDate ||
    isBefore(goal.startDate, today) ||
    isSameDay(goal.startDate, today);

  const startDateLabel = useMemo(
    () =>
      goal.startDate
        ? getStartTillEndLabel({
            startDate: goal.startDate,
            isShort: true,
            today,
            t,
          })
        : undefined,
    [goal.startDate, today],
  );

  const deadlineOverdue = useMemo(
    () =>
      !!goal.deadline && !goal.completedAt && isBefore(goal.deadline, today),
    [goal.deadline, goal.completedAt, today],
  );

  const deadlineUrgent = useMemo(
    () =>
      !!goal.deadline &&
      !goal.completedAt &&
      !deadlineOverdue &&
      differenceInDays(goal.deadline, today) < 8,
    [goal.deadline, goal.completedAt, today, deadlineOverdue],
  );

  const openMetricsForm = useOpenGoalMetricsForm();

  const hasProgressMetric =
    !!goal.metric?.id && !countMetricOptions.includes(goal.metric?.id);

  const updateProgressDisabled =
    !!onUpdateProgress &&
    (!hasProgressMetric || !!goal.frozenAt || !!goal.archivedAt);

  const onProgressSelectClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openMetricsForm(goal);
  };

  const onStartDateClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openStartDateMenu();
  };

  const onStartDateChange = (date?: Timestamp) => {
    updateGoal({ goal: { id: goal.id, startDate: date ?? null } });
    closeStartDateMenu();
  };

  const onDeadlineClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openDeadlineMenu();
  };

  const onDeadlineChange = (date?: Timestamp) => {
    updateGoal({ goal: { id: goal.id, deadline: date ?? null } });
    closeDeadlineMenu();
  };

  const onImpactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openImpactMenu();
  };

  const onImpactChange = (value: ImpactLevel | null) => {
    updateGoal({ goal: { id: goal.id, impact: value } });
    closeImpactMenu();
  };

  const impactLabelMap: Record<ImpactLevel, string> = {
    [ImpactLevel.High]: t('goal.impact.options.high.label'),
    [ImpactLevel.Medium]: t('goal.impact.options.medium.label'),
    [ImpactLevel.Low]: t('goal.impact.options.low.label'),
  };

  return (
    <>
      <CtaContainer
        id={goal.id}
        title={goal.name}
        isFrozen={!!goal.frozenAt}
        onClick={onClick}
        preventInteraction={preventInteraction}
      >
        <Styled.Container
          onContextMenu={openMenu}
          $isArchived={!!goal.archivedAt}
          $isSelected={showSelection}
          $isDropTarget={isDropTarget}
        >
          {/* image and title */}
          <Styled.DataContainer
            $type={Styled.DataTypes.Title}
            $isFrozen={!!goal.frozenAt}
          >
            <Styled.IllustrationContainer>
              <Styled.ImageContainer>
                {goal.image ? (
                  <Styled.Image
                    src={goal.image.url}
                    alt={goal.name}
                    hash={goal.image.blurHash}
                  />
                ) : (
                  <Icon icon={IconFormIcons[goal.iconName!] ?? Target} />
                )}
              </Styled.ImageContainer>

              {!!goal.completedAt && (
                <Styled.CompletedContainer>
                  <Icon icon={Check} />
                </Styled.CompletedContainer>
              )}

              {!!color && <Styled.ColorDot $color={color} />}
            </Styled.IllustrationContainer>

            <Styled.Title $isCompleted={!!goal.completedAt}>
              {goal.name}
            </Styled.Title>

            {goal.frozenAt && (
              <Styled.IconContainer>
                <Icon icon={Lock} />
              </Styled.IconContainer>
            )}
          </Styled.DataContainer>

          {!isDragPreview && (
            <>
              {/* progress */}
              <Styled.DataContainer
                $type={Styled.DataTypes.Progress}
                $isFrozen={!!goal.frozenAt}
                $isClickable={!goal.frozenAt && end === Infinity}
                onClick={
                  !goal.frozenAt && end === Infinity
                    ? onProgressSelectClick
                    : undefined
                }
              >
                {end !== Infinity ? (
                  <Styled.DataButton
                    onClick={
                      updateProgressDisabled ? undefined : _onUpdateProgress
                    }
                    as={updateProgressDisabled ? 'span' : undefined}
                  >
                    <CircleProgress
                      percentage={completionPercentage}
                      isPrimary={!goal.frozenAt}
                      isSecondary={!!goal.frozenAt}
                    />
                  </Styled.DataButton>
                ) : (
                  <Styled.SelectPlaceholder>
                    {t('general.labels.select')}
                  </Styled.SelectPlaceholder>
                )}
              </Styled.DataContainer>

              {/* impact */}
              <Styled.DataContainer
                ref={impactRef}
                $type={Styled.DataTypes.Impact}
                $isFrozen={!!goal.frozenAt}
                $isClickable={!goal.frozenAt}
                onClick={!goal.frozenAt ? onImpactClick : undefined}
              >
                {goal.impact ? (
                  <Styled.DataButton $isSmallFont $impactLevel={goal.impact}>
                    <Icon icon={ThunderLightningFilled} />
                    {impactLabelMap[goal.impact]}
                  </Styled.DataButton>
                ) : (
                  <Styled.SelectPlaceholder $forceShow={impactMenuOpen}>
                    {t('general.labels.select')}
                  </Styled.SelectPlaceholder>
                )}
              </Styled.DataContainer>

              {/* start date */}
              <Styled.DataContainer
                ref={startDateRef}
                $type={Styled.DataTypes.StartDate}
                $isFrozen={!!goal.frozenAt}
                $isClickable={!goal.frozenAt}
                onClick={!goal.frozenAt ? onStartDateClick : undefined}
              >
                {startDateLabel ? (
                  <Styled.DataButton>{startDateLabel}</Styled.DataButton>
                ) : (
                  <Styled.SelectPlaceholder $forceShow={startDateMenuOpen}>
                    {t('general.labels.select')}
                  </Styled.SelectPlaceholder>
                )}
              </Styled.DataContainer>

              {/* deadline */}
              <Styled.DataContainer
                ref={deadlineRef}
                $type={Styled.DataTypes.Deadline}
                $isFrozen={!!goal.frozenAt}
                $isClickable={!goal.frozenAt}
                onClick={!goal.frozenAt ? onDeadlineClick : undefined}
              >
                {goal.deadline ? (
                  <Styled.DataButton
                    $isSmallFont
                    $background={
                      deadlineOverdue
                        ? theme.palette.error.light
                        : deadlineUrgent
                          ? theme.palette.warning.light
                          : theme.palette.grey[100]
                    }
                    $color={
                      deadlineOverdue
                        ? theme.palette.error.dark
                        : deadlineUrgent
                          ? theme.palette.warning.dark
                          : theme.palette.text.primary
                    }
                  >
                    {started && !goal.completedAt ? (
                      <>
                        <Icon icon={FlagFilled} />
                        <TimeLeft date={goal.deadline} isMidShort />
                      </>
                    ) : (
                      <Timeframe endDate={goal.deadline} isShort />
                    )}
                  </Styled.DataButton>
                ) : (
                  <Styled.SelectPlaceholder $forceShow={deadlineMenuOpen}>
                    {t('general.labels.select')}
                  </Styled.SelectPlaceholder>
                )}
              </Styled.DataContainer>

              {/* tasks */}
              <Styled.DataContainer
                $type={Styled.DataTypes.Tasks}
                $isFrozen={!!goal.frozenAt}
              >
                <Styled.IconContainer>
                  <Icon icon={CheckCircle2} />
                </Styled.IconContainer>
                <span>{uncompletedTasks}</span>
              </Styled.DataContainer>

              {/* life area */}
              {!hideLifeArea && (
                <Styled.DataContainer
                  $type={Styled.DataTypes.LifeArea}
                  $isFrozen={!!goal.frozenAt}
                >
                  {lifeArea?.name ? (
                    <Styled.OverflowSpan>{lifeArea.name}</Styled.OverflowSpan>
                  ) : (
                    <Styled.SelectPlaceholder>
                      {t('general.labels.select')}
                    </Styled.SelectPlaceholder>
                  )}
                </Styled.DataContainer>
              )}

              {/* context menu */}
              <Styled.DataContainer
                $type={Styled.DataTypes.Interactions}
                $isFrozen={!!goal.frozenAt}
              >
                {hasChildren && (
                  <IconButton
                    icon={ChevronLeft}
                    onClick={onToggleExpand}
                    rotation={isExpanded ? -90 : 0}
                    color={Colors.Default}
                  />
                )}
              </Styled.DataContainer>
            </>
          )}
        </Styled.Container>
      </CtaContainer>

      {!!menuPosition && !theme.isMobile && (
        <GoalContextMenu
          id={goal.id}
          location={menuPosition}
          referenceElement={undefined}
          onClose={closeMenu}
          excludedOptions={excludedContextMenuOptions}
        />
      )}

      {startDateMenuOpen && (
        <DatepickerWithOptions
          referenceElement={startDateRef}
          value={goal.startDate ?? undefined}
          onChange={onStartDateChange}
          dateFormat={user?.settings?.dateFormat ?? initialDateFormat}
          weekStartsOn={getWeekStartsOn(user)}
          icon={IconOptions.Calendar}
          canRemoveValue={!!goal.startDate}
          onOutside={closeStartDateMenu}
        />
      )}

      {impactMenuOpen && (
        <ImpactPickerMenu
          referenceElement={impactRef}
          value={goal.impact ?? null}
          onChange={onImpactChange}
          onClose={closeImpactMenu}
        />
      )}

      {deadlineMenuOpen && (
        <DatepickerWithOptions
          referenceElement={deadlineRef}
          value={goal.deadline ?? undefined}
          onChange={onDeadlineChange}
          dateFormat={user?.settings?.dateFormat ?? initialDateFormat}
          weekStartsOn={getWeekStartsOn(user)}
          icon={IconOptions.Calendar}
          canRemoveValue={!!goal.deadline}
          onOutside={closeDeadlineMenu}
        />
      )}
    </>
  );
};
